import { Box, BoxProps, Button } from '@mui/material';
import { Control, useFieldArray } from 'react-hook-form';
import { FeatureValueValues, FinancesDrawerFormValues } from '../RightDrawerSchema';
import { useTranslation } from 'react-i18next';
import { EnhancedTable } from '../../../../../common/table/EnhancedTable';
import { HeadCell } from '../../../../../common/table/EnhancedTableHead';
import { FinancesFeaturesDialog } from './FinancesFeaturesDialog';
import { useState } from 'react';
import { useQuery } from 'react-query';
import RestHttpClient from '../../../../../common/RestHttpClient';
import { DialogState } from '../../../../classification/groups/GroupPage';

interface FeatureRow {
  id: string;
  name: string;
  isSystem: string;
  value: string;
}

interface Props extends BoxProps {
  control: Control<FinancesDrawerFormValues>;
}

export const FinancesFeatures = ({ control, ...other }: Props) => {
  const { t } = useTranslation();
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState<number | null>(null);
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.Closed);

  const { data: features = [] } = useQuery({
    queryKey: ['classification', 'feature', 'definitions'],
    queryFn: () => RestHttpClient.getFeatureDefinitions(),
    select: (x) => x.data,
  });

  const headCells: readonly HeadCell<FeatureRow>[] = [
    {
      id: 'name',
      disablePadding: false,
      label: t('classification.features.name'),
      allowSorting: true,
      allowFiltering: true,
    },
    {
      id: 'value',
      disablePadding: false,
      label: t('classification.features.value'),
      allowSorting: true,
      allowFiltering: true,
    },
    {
      id: 'isSystem',
      disablePadding: false,
      label: t('classification.features.isSystem'),
      allowSorting: true,
      allowFiltering: true,
    },
  ];

  const { fields, append, update } = useFieldArray({
    control,
    name: 'features',
  });

  const handleRowClick = (e: React.MouseEvent<unknown>, id: string) => {
    const featureIndex = fields.findIndex((field) => field.id === id);
    if (featureIndex !== -1) {
      setSelectedFeatureIndex(featureIndex);
      setDialogState(DialogState.Edit);
    }
  };

  const handleAddFeature = () => {
    // Bereits hinzugefügte Features filtern
    const existingFeatureIds = fields.map((f) => f.featureId);
    const availableFeatures = features.filter((f) => !existingFeatureIds.includes(f.id));

    if (availableFeatures.length === 0) return;

    const firstAvailableFeature = availableFeatures[0];

    append({
      featureId: firstAvailableFeature.id,
      name: firstAvailableFeature.name,
      value: '',
      // Noch kein Override — das Backend markiert es erst, wenn der Wert vom abgeleiteten abweicht.
      isOverwritten: false,
      isSystem: firstAvailableFeature.isSystem,
    });
    setDialogState(DialogState.Add);
    setSelectedFeatureIndex(existingFeatureIds.length);
  };

  const handleDialogSubmit = (featureValue: FeatureValueValues) => {
    // `!== null` statt truthy: Index 0 ist gültig, vorher liess sich das erste Merkmal nicht bearbeiten.
    if (selectedFeatureIndex !== null) {
      update(selectedFeatureIndex, {
        ...featureValue,
        // Der Dialog ist die einzige Stelle, an der ein Merkmalswert manuell gesetzt wird. Nur hier darf das
        // Override-Flag entstehen — das Backend leitet es bewusst nicht aus einem Wertvergleich ab.
        isOverwritten: featureValue.isSystem ? true : featureValue.isOverwritten,
      });
    }
    handleDialogClose();
  };

  const handleDialogClose = () => {
    setDialogState(DialogState.Closed);
    setSelectedFeatureIndex(null);
  };

  const rows = fields.map((feature) => ({
    id: feature.id,
    name: feature.name,
    value: feature.value,
    isSystem: feature.isSystem ? 'ja' : 'nein',
  }));

  return (
    <Box {...other}>
      <Box sx={{ m: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ marginBottom: 16 }}>
          {t('deals.new.financesConfiguration.calculationDrawer.features.drawerTitle')}
        </h2>
        <Button
          variant="contained"
          onClick={handleAddFeature}
          sx={{ mb: 2 }}
          disabled={fields.length >= features.length}
        >
          {t('classification.features.addFeature')}
        </Button>
      </Box>

      <EnhancedTable
        rows={rows}
        headCells={headCells}
        hideCheckbox={true}
        variant="default"
        onRowClick={handleRowClick}
      />

      {selectedFeatureIndex !== null && (
        <FinancesFeaturesDialog
          state={dialogState}
          onSubmit={handleDialogSubmit}
          onClose={handleDialogClose}
          control={control}
          featureIndex={selectedFeatureIndex}
          features={features}
        />
      )}
    </Box>
  );
};
